class ApiService {
  private static instance: ApiService;
  private unauthorizedCallback?: () => void;

  private constructor() {}

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  setUnauthorizedCallback(callback: () => void) {
    this.unauthorizedCallback = callback;
  }

  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Merge default options with provided options
    const defaultOptions: RequestInit = {
      credentials: 'include', // Include cookies for authentication
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    if (response.status === 403) {
      // Navigate to unauthorized page
      if (this.unauthorizedCallback) {
        this.unauthorizedCallback();
      } else {
        window.location.href = '/unauthorized';
      }
    }

    if (response.status === 401) {
      // Trigger unauthorized event for sign out
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    return response;
  }

  // Error Monitoring API (Organization-level - deprecated)
  async getErrorGroups(token: string, status: 'active' | 'resolved' | 'ignored' = 'active', cursor?: number, limit: number = 50) {
    const params = new URLSearchParams({ status, limit: limit.toString() });
    if (cursor) params.append('cursor', cursor.toString());

    const response = await this.fetch(`/api/errors/groups?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch error groups');
    return response.json();
  }

  async getErrorsByGroup(token: string, groupId: number, cursor?: number, limit: number = 50) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor.toString());

    const response = await this.fetch(`/api/errors/groups/${groupId}/errors?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch errors');
    return response.json();
  }

  async updateErrorGroupStatus(token: string, groupId: number, status: 'active' | 'resolved' | 'ignored') {
    const response = await this.fetch(`/api/errors/groups/${groupId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update error group status');
    return response.json();
  }

  async getErrorStats(token: string) {
    const response = await this.fetch('/api/errors/stats', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch error stats');
    return response.json();
  }

  // Application-scoped Error Monitoring API
  async getErrorGroupsByApp(token: string, appId: string, status: 'active' | 'resolved' | 'ignored' = 'active', cursor?: number, limit: number = 50) {
    const params = new URLSearchParams({ status, limit: limit.toString() });
    if (cursor) params.append('cursor', cursor.toString());

    const response = await this.fetch(`/api/applications/${appId}/error-groups?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch error groups');
    return response.json();
  }

  async getErrorsByGroupForApp(token: string, appId: string, groupId: number, cursor?: number, limit: number = 50) {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor.toString());

    const response = await this.fetch(`/api/applications/${appId}/error-groups/${groupId}/errors?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch errors');
    return response.json();
  }

  async updateErrorGroupStatusForApp(token: string, appId: string, groupId: number, status: 'active' | 'resolved' | 'ignored') {
    const response = await this.fetch(`/api/applications/${appId}/error-groups/${groupId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status })
    });
    if (!response.ok) throw new Error('Failed to update error group status');
    return response.json();
  }

  async getErrorStatsForApp(token: string, appId: string) {
    const response = await this.fetch(`/api/applications/${appId}/error-stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch error stats');
    return response.json();
  }
}

export const apiService = ApiService.getInstance();